import React, { useState, useEffect, useMemo, FC, useCallback } from "react";
import { FileText, Database, Code, User, Clock, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { useJupyterWorkspace } from "../contexts/JupyterWorkspaceContext";
import { useupdateSubmissionMutation } from "../api/submissionApi";
import { toast } from "sonner";
import { getContents } from "../api/jupyterManagementApi";
import { useAuth } from "../App";
import {
  useSendEnhancedMessageMutation,
  SendGroupMessageRequest,
} from "../api/CommunicationApi";

export interface FileItem {
  name: string;
  path: string;
  type: "file" | "directory" | "notebook";
  size?: number;
  lastmodified?: string;
  mimetype?: string | null;
  content?: any | null;
  created?: string | null;
  format?: string | null;
  writable?: boolean;
}

interface SubmitAssignmentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  files?: FileItem[];
  onAssignmentSubmit?: (submission: {
    checkpoint: string;
    files: FileItem[];
    comments?: string;
  }) => void;
  project?: any;
  isDarkMode?: boolean;
}

const inferBadge = (file: FileItem) => {
  if (file.type === "notebook" || file.name.endsWith(".ipynb"))
    return { icon: FileText, cls: "text-yellow-500" } as const;
  if (
    file.name.endsWith(".csv") ||
    file.name.endsWith(".xls") ||
    file.name.endsWith(".xlsx") ||
    file.name.endsWith(".json") ||
    file.name.endsWith(".parquet") ||
    file.name.endsWith(".h5") ||
    file.name.endsWith(".zip")
  )
    return { icon: Database, cls: "text-blue-500" } as const;
  if (file.name.endsWith(".py"))
    return { icon: Code, cls: "text-green-500" } as const;
  return { icon: FileText, cls: "text-gray-500" } as const;
};

const formatRelativeTime = (dateString?: string | null) => {
  if (!dateString) return "recently";
  const date = new Date(dateString);
  const now = new Date();
  const secs = Math.floor((now.getTime() - date.getTime()) / 1000);
  const units = [
    { name: "year", s: 31536000 },
    { name: "month", s: 2592000 },
    { name: "week", s: 604800 },
    { name: "day", s: 86400 },
    { name: "hour", s: 3600 },
    { name: "minute", s: 60 },
  ];
  for (const u of units) {
    const n = Math.floor(secs / u.s);
    if (n >= 1) return `${n} ${u.name}${n > 1 ? "s" : ""} ago`;
  }
  return "just now";
};

const FileRow: FC<{
  file: FileItem;
  isSelected: boolean;
  onToggle: (path: string) => void;
  isDarkMode?: boolean;
}> = ({ file, isSelected, onToggle, isDarkMode = false }) => {
  const badge = inferBadge(file);
  return (
    <div
      className={`flex items-center p-2 rounded-lg border transition-all cursor-pointer ${
        isSelected
          ? "bg-indigo-50 border-indigo-200 dark:bg-indigo-900/20 dark:border-indigo-500/50"
          : "bg-gray-50 border-gray-200 hover:bg-gray-100 dark:bg-[#0d1117] dark:border-[#30363d] dark:hover:bg-[#161b22]"
      }`}
      onClick={() => onToggle(file.path)}
    >
      <input
        type="checkbox"
        className="h-3.5 w-3.5 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-[#30363d] rounded mr-2 flex-shrink-0 dark:bg-[#161b22]"
        checked={isSelected}
        onChange={(e) => {
          e.stopPropagation();
          onToggle(file.path);
        }}
        onClick={(e) => e.stopPropagation()}
        aria-label={`Select ${file.name}`}
      />
      <div className="mr-2 flex-shrink-0">
        <badge.icon className={`h-4 w-4 ${badge.cls}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p
          className="font-medium text-xs text-gray-800 dark:text-[#f0f6fc] truncate"
          title={file.name}
        >
          {file.name}
        </p>
        <p className="text-[10px] text-gray-500 dark:text-[#8b949e] truncate mt-0.5">
          {file.size ? `${file.size} bytes` : ""}
          {file.lastmodified
            ? ` • ${formatRelativeTime(file.lastmodified)}`
            : ""}
        </p>
      </div>
    </div>
  );
};

export default function SubmitAssignmentDialog(
  props: SubmitAssignmentDialogProps,
) {
  const {
    isOpen,
    onClose,
    files = [],
    onAssignmentSubmit,
    project,
    isDarkMode = false,
  } = props;
  const { mutateAsync: updateSubmission, isPending: isSubmitting } =
    useupdateSubmissionMutation();
  const { workspace } = useJupyterWorkspace();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [comments, setComments] = useState<string>("");
  const [selectedCheckpointId, setSelectedCheckpointId] = useState<string>("");
  const [additionalFiles, setAdditionalFiles] = useState<FileItem[]>([]);

  const sendMessageMutation = useSendEnhancedMessageMutation();
  const { user } = useAuth();

  useEffect(() => {
    const fetchDatasetFiles = async () => {
      if (!isOpen || !files.length) return;

      const fetchRecursively = async (dirPath: string): Promise<FileItem[]> => {
        try {
          const res = await getContents(dirPath, workspace.workspaceType === 'local');
          const contents: FileItem[] = res.content || [];

          const filesInDir = contents.filter((f) => f.type !== "directory");
          const subDirs = contents.filter(
            (f) => f.type === "directory" && !f.name.startsWith("."),
          );

          const subDirFiles = await Promise.all(
            subDirs.map((dir) => fetchRecursively(dir.path)),
          );

          return [...filesInDir, ...subDirFiles.flat()];
        } catch (error) {
          console.error(`Failed to fetch contents for ${dirPath}:`, error);
          return [];
        }
      };

      let allDatasetFiles: FileItem[] = [];

      const topLevelDirs = files.filter(
        (f) => f.type === "directory" && !f.name.startsWith("."),
      );

      if (topLevelDirs.length > 0) {
        try {
          const allContents = await Promise.all(
            topLevelDirs.map((dir) => fetchRecursively(dir.path)),
          );
          allDatasetFiles = allContents.flat();
        } catch (error) {
          console.error("Failed to fetch workspace dataset files:", error);
        }
      }

      // Add course datasets from project data if present
      if (project?.datasets && Array.isArray(project.datasets)) {
        const courseDatasets = project.datasets.flatMap((dataset: any) => {
          if (dataset.s3_keys && Array.isArray(dataset.s3_keys)) {
            return dataset.s3_keys.map(
              (file: any) =>
                ({
                  name: file.fileName || file.key || "unknown_file",
                  path: `course_datasets/${file.fileName || file.key}`,
                  type: "file" as const,
                  size: file.size || 0,
                  lastmodified: file.lastModified || new Date().toISOString(),
                  mimetype: file.mimetype || null,
                  content: null,
                  created: null,
                  format: null,
                  writable: false,
                  presignedUrl: file.presignedUrl || file.url,
                }) as FileItem & { presignedUrl?: string },
            );
          }
          return [];
        });
        allDatasetFiles = [...allDatasetFiles, ...courseDatasets];
      }

      if (
        project?.datasetS3Details &&
        Array.isArray(project.datasetS3Details)
      ) {
        const legacyDatasets = project.datasetS3Details.map(
          (file: any) =>
            ({
              name: file.fileName || file.key || "unknown_file",
              path: `course_datasets/${file.fileName || file.key}`,
              type: "file" as const,
              size: file.size || 0,
              lastmodified: file.lastModified || new Date().toISOString(),
              mimetype: file.mimetype || null,
              content: null,
              created: null,
              format: null,
              writable: false,
              presignedUrl: file.presignedUrl || file.url,
            }) as FileItem & { presignedUrl?: string },
        );
        allDatasetFiles = [...allDatasetFiles, ...legacyDatasets];
      }

      setAdditionalFiles(allDatasetFiles);
    };

    fetchDatasetFiles();
  }, [isOpen, files]);
  const sendGroupSubmissionNotification = useCallback(
    async (params: {
      groupId: string;
      groupName: string;
      projectName: string;
      checkpointName: string;
      submittedBy: { name: string; email: string; id: string };
      fileNames: string[];
      teamMembers: Array<{ name: string; email: string; id: string }>;
      courseName: string;
    }) => {
      const {
        groupId,
        groupName,
        projectName,
        checkpointName,
        submittedBy,
        fileNames,
        teamMembers,
        courseName,
      } = params;
      const now = new Date();
      const dateTime = now.toLocaleString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      const fileList = Array.from(selectedIds)
        .map((path) => {
          if (!path || typeof path !== "string") return null;
          const filename = path.split("/").pop();
          return filename || null;
        })
        .filter(Boolean) as string[];
      const teamMembersList = teamMembers
        .map((member) => `${member.name} : ${member.email}`)
        .join("\n");

      const messageContent = `${courseName}-${projectName}: ${groupName}-Project Submission Update Project ${projectName} has been submitted for ${checkpointName} on ${dateTime}.

      Submitted by: ${submittedBy.name}-${submittedBy.email}

      Files Included: ${fileList}
      
      Team Members:
      ${teamMembersList}`;

      const recipientIds = teamMembers
        .map((member) => member.id)
        .filter((id) => id !== submittedBy.id);

      if (recipientIds.length === 0) {
        console.log("No other team members to notify");
        return;
      }

      const payload: SendGroupMessageRequest = {
        recipientId: recipientIds,
        groupName: groupName,
        subject: `${groupName} : Project Submission Update`,
        content: messageContent,
      };
      try {
        await sendMessageMutation.mutateAsync(payload);
        console.log("Group notification sent successfully");
      } catch (error) {
        console.error("Failed to send group notification:", error);
      }
    },
    [sendMessageMutation, selectedIds],
  );

  useEffect(() => {
    if (!isOpen) {
      setSelectedIds(new Set());
      setComments("");
      setSelectedCheckpointId("");
      setAdditionalFiles([]);
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isSubmitting) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isSubmitting, onClose]);

  const filesToDisplay = useMemo(() => {
    const rootFiles = (files || []).filter(
      (f) => f && f.path && f.type !== "directory",
    );
    return [...rootFiles, ...additionalFiles];
  }, [files, additionalFiles]);

  const { notebooks, datasets, scripts, otherFiles } = useMemo(() => {
    const notebooks: FileItem[] = [];
    const datasets: FileItem[] = [];
    const scripts: FileItem[] = [];
    const otherFiles: FileItem[] = [];

    const DATASET_EXTS = new Set([
      "csv",
      "xls",
      "xlsx",
      "md",
      "txt",
      "json",
      "parquet",
      "h5",
      "zip",
    ]);
    const SCRIPT_EXTS = new Set(["py", "js", "ts"]);

    filesToDisplay.forEach((f) => {
      const parts = f.name.split(".");
      const ext = parts.length > 1 ? parts.pop()?.toLowerCase() || "" : "";

      // 1. Notebooks
      if (f.type === "notebook" || ext === "ipynb") {
        notebooks.push(f);
        return;
      }

      // 2. Scripts
      if (SCRIPT_EXTS.has(ext)) {
        scripts.push(f);
        return;
      }

      // 3. Datasets (with root filtering)
      if (DATASET_EXTS.has(ext)) {
        const pathParts = f.path.split("/");
        const isUnderDatasets =
          pathParts.includes("datasets") ||
          pathParts.includes("course_datasets");

        let isRoot = false;
        const dIdx = pathParts.indexOf("datasets");
        const cdIdx = pathParts.indexOf("course_datasets");

        if (dIdx !== -1) {
          isRoot = pathParts.length === dIdx + 2;
        } else if (cdIdx !== -1) {
          isRoot = pathParts.length === cdIdx + 2;
        } else {
          const folderPath =
            workspace?.folderPath || (project?.id ? `${project.id}` : "");
          if (folderPath && f.path.startsWith(folderPath)) {
            const rel = f.path
              .substring(folderPath.length)
              .replace(/^\/+/, "");
            isRoot = !rel.includes("/");
          } else {
            isRoot = !f.path.includes("/");
          }
        }

        if (isRoot) {
          datasets.push(f);
          return;
        } else if (isUnderDatasets) {
          // Hide dataset files that are in subdirectories of 'datasets'
          return;
        }
      }

      // 4. Other Files
      otherFiles.push(f);
    });

    return { notebooks, datasets, scripts, otherFiles };
  }, [filesToDisplay, workspace, project]);

  const checkpoints: Array<{ id: string; title: string; due_date?: string }> =
    useMemo(() => {
      const cps = Array.isArray((project as any)?.checkpoints)
        ? ((project as any).checkpoints as Array<{
            id: string;
            title: string;
            due_date?: string;
          }>)
        : [];
      return cps;
    }, [project]);

  const onToggle = useCallback(
    (path: string) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        const isNotebook = notebooks.some((n) => n.path === path);

        if (next.has(path)) {
          next.delete(path);
        } else {
          if (isNotebook) {
            // Deselect all other notebooks
            notebooks.forEach((n) => {
              if (next.has(n.path)) next.delete(n.path);
            });
          }
          next.add(path);
        }
        return next;
      });
    },
    [notebooks],
  );

  const handleSubmit = useCallback(async () => {
    if (!selectedCheckpointId || !(project as any)?.id) {
      toast.error("Select a checkpoint first.");
      return;
    }
    const selected = filesToDisplay.filter((f) => selectedIds.has(f.path));
    if (selected.length === 0) {
      toast.error("Select at least one file to submit.");
      return;
    }
    const filenamesToSubmit = selected
      .map((f) => {
        // If file is in a subdirectory, send the relative path from project root
        // Example: if f.path is 'project1/datasets/data.csv' and folderPath is 'project1',
        // filenamesToSubmit should include 'datasets/data.csv'
        const folderPath = workspace?.folderPath || `project_${project?.id}`;
        if (f.path.startsWith(folderPath)) {
          return f.path.substring(folderPath.length + 1);
        }
        return f.name;
      })
      .filter(Boolean);
    if (filenamesToSubmit.length === 0) {
      toast.error("No filenames resolved for submission.");
      return;
    }
    const cp = checkpoints.find((c) => c.id === selectedCheckpointId);
    const summary =
      (comments || "").trim() ||
      (cp ? `Submission for ${cp.title}` : "Submission");

    const loading = toast.loading("Submitting ...");
    let isSuccess = false;
    const baseBody = {
      projectId: (project as any).id,
      checkpointId: selectedCheckpointId,
      submissionSummary: summary,
      metadata: {
        language: "python",
        notebookType: "jupyter",
        environment: "conda-py38",
      },
      timeSpentMinutes: 120,
      max_attempts: project?.attempts || 1,
      filenamesToSubmit,
      files: undefined as any,
      existingFileIds: undefined as any,
    };
    try {
      await updateSubmission(baseBody as any);
      toast.dismiss(loading);
      toast.success("Submission uploaded successfully.");
      onAssignmentSubmit?.({
        checkpoint: selectedCheckpointId,
        files: selected,
        comments,
      });
      isSuccess = true;
      onClose();
    } catch (e: any) {
      if (
        e.status === 409 &&
        e.response?.data?.data?.data?.allowUpdate === false
      ) {
        const ok = window.confirm(
          e.response?.data?.data?.data?.message ||
            "Already submitted. Update existing submission?",
        );
        if (!ok) {
          toast.dismiss(loading);
          toast.info("Submission not updated");
          return;
        }
        const newAttemptBody = {
          ...baseBody,
          allowUpdate: true,
        };
        try {
          await updateSubmission(newAttemptBody as any);
          toast.dismiss(loading);
          toast.success("Submission uploaded successfully.");
          onAssignmentSubmit?.({
            checkpoint: selectedCheckpointId,
            files: selected,
            comments,
          });
          isSuccess = true;
          onClose();
          return;
        } catch (err: any) {
          toast.dismiss(loading);
          toast.error(
            `Submission failed: ${
              err.response?.data?.data?.data?.message ||
              err.response?.data?.data?.message ||
              err.response?.data?.message ||
              "Unknown error"
            }`,
          );
          return;
        }
      }
      toast.dismiss(loading);
      toast.error(
        `Submission failed: ${
          e.response?.data?.data?.data?.message ||
          e.response?.data?.data?.message ||
          e.response?.data?.message ||
          "Unknown error"
        }`,
      );
    } finally {
      if (isSuccess) {
        if (project?.isGroupProject && project?.students?.[0]?.groupId) {
          const groupData = project.students[0];
          const selectedCheckpoint = project.checkpoints.find(
            (cp: any) => cp.id === selectedCheckpointId,
          );
          await sendGroupSubmissionNotification({
            groupId: groupData.groupId,
            groupName: groupData.groupName,
            projectName: project.title,
            checkpointName: selectedCheckpoint?.title || "Unknown Checkpoint",
            submittedBy: {
              name: user?.name || "",
              email: user?.email || "",
              id: user?.id || "",
            },
            fileNames: Array.from(selectedIds),
            teamMembers: groupData.members.map((m: any) => ({
              id: m.id,
              name: m.name,
              email: m.email,
            })),
            courseName: project.courseName || "Course",
          });
        }
      }
    }
  }, [
    selectedCheckpointId,
    project,
    filesToDisplay,
    selectedIds,
    checkpoints,
    comments,
    updateSubmission,
    onAssignmentSubmit,
    onClose,
    user,
    sendGroupSubmissionNotification,
  ]);

  if (!isOpen) return null;

  const canInteract = Boolean(selectedCheckpointId);
  const hasSelection = selectedIds.size > 0;
  // Check if selected checkpoint is overdue
  const selectedCheckpoint = checkpoints.find(
    (cp) => cp.id === selectedCheckpointId,
  );
  const isSelectedOverdue =
    selectedCheckpoint &&
    selectedCheckpoint.due_date &&
    new Date() > new Date(selectedCheckpoint.due_date);
  const canSubmit =
    canInteract && hasSelection && !isSubmitting && !isSelectedOverdue;

  return (
    <div
      className={`fixed inset-0 bg-black/40 bg-opacity-20 flex justify-center items-center z-50 ${isDarkMode ? "dark" : ""}`}
    >
      <div
        className={`fixed inset-0 bg-black/40 bg-opacity-20 flex justify-center items-center z-50 ${isDarkMode ? "dark" : ""}`}
      >
        <div className="bg-white dark:bg-[#161b22] rounded-xl w-full max-w-2xl max-h-[85vh] transform transition-all overflow-y-auto dark:border dark:border-[#30363d]">
          <div className="p-2 px-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-[#f0f6fc]">
              Submit Assignment
            </h2>

            <p className="text-sm text-gray-600 dark:text-[#8b949e] mt-1">
              Select a checkpoint to proceed, then choose files and add
              comments.
            </p>
          </div>

          <div className="px-6 pb-2">
            <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-900/30 rounded-lg p-2 px-4">
              <h3 className="font-bold text-lg text-gray-800 dark:text-[#f0f6fc]">
                {(project as any)?.title} Assignment
              </h3>
              <div className="flex items-center text-sm text-gray-600 dark:text-[#8b949e] mt-2">
                <div className="flex items-center mr-4">
                  <User className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-1" />
                  <span>{(project as any)?.creatorName || "Instructor"}</span>
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-1" />
                  <span>{formatRelativeTime((project as any)?.createdAt)}</span>
                </div>
              </div>
              {(project as any)?.isGroupProject && (
                <div className="mt-3">
                  <h3 className="font-bold text-lg text-gray-800 dark:text-[#f0f6fc] mb-2">
                    Group Members
                  </h3>
                  {(project as any)?.students?.[0]?.members?.map(
                    (member: any) => (
                      <div
                        key={member.id}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-[#30363d] rounded-md px-3 py-2 mb-2"
                      >
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-800 dark:text-[#f0f6fc]">
                            {member.name}
                          </span>
                          <span className="text-sm text-gray-600 dark:text-[#8b949e]">
                            {member.email}
                          </span>
                        </div>
                        {member.role === "leader" && (
                          <span className="text-xs text-blue-600 dark:text-[#f0883e] font-semibold mt-1 sm:mt-0">
                            Leader
                          </span>
                        )}
                      </div>
                    ),
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="px-6 pb-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-[#8b949e] mb-1">
              Checkpoint
            </label>
            <select
              className="w-full p-2.5 border border-gray-300 dark:border-[#30363d] rounded-lg focus:border-indigo-500 transition bg-white dark:bg-[#0d1117] dark:text-[#f0f6fc]"
              value={selectedCheckpointId}
              onChange={(e) => setSelectedCheckpointId(e.target.value)}
            >
              <option value="" disabled>
                Select a checkpoint
              </option>
              {checkpoints.map((cp) => {
                const isOverdue =
                  cp.due_date && new Date() > new Date(cp.due_date);
                return (
                  <option key={cp.id} value={cp.id} disabled={!!isOverdue}>
                    {cp.title}
                    {isOverdue ? " (Overdue - Cannot submit)" : ""}
                  </option>
                );
              })}
            </select>
            {!selectedCheckpointId && (
              <p className="text-xs text-gray-500 dark:text-[#8b949e] mt-2">
                Choose a checkpoint to enable file selection and comments.
              </p>
            )}
          </div>

          {canInteract && (
            <div className="px-6 pb-6 max-h-[40vh] overflow-y-auto">
              <h3 className="font-semibold text-gray-800 dark:text-[#f0f6fc] mb-3">
                Files to Submit
              </h3>
              <p className="text-sm text-gray-600 dark:text-[#8b949e] mb-4">
                Select the files to include with this checkpoint.
              </p>

              {notebooks.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-medium text-gray-700 dark:text-[#c9d1d9] mb-2 text-sm">
                    Notebooks
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {notebooks.map((file) => (
                      <FileRow
                        key={file.path}
                        file={file}
                        isSelected={selectedIds.has(file.path)}
                        onToggle={onToggle}
                        isDarkMode={isDarkMode}
                      />
                    ))}
                  </div>
                </div>
              )}

              {datasets.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-medium text-gray-700 dark:text-[#c9d1d9] mb-2 text-sm">
                    Uploaded Datasets
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {datasets.map((file) => (
                      <FileRow
                        key={file.path}
                        file={file}
                        isSelected={selectedIds.has(file.path)}
                        onToggle={onToggle}
                        isDarkMode={isDarkMode}
                      />
                    ))}
                  </div>
                </div>
              )}

              {scripts.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-medium text-gray-700 dark:text-[#c9d1d9] mb-2 text-sm">
                    Scripts
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {scripts.map((file) => (
                      <FileRow
                        key={file.path}
                        file={file}
                        isSelected={selectedIds.has(file.path)}
                        onToggle={onToggle}
                        isDarkMode={isDarkMode}
                      />
                    ))}
                  </div>
                </div>
              )}

              {otherFiles.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-2 text-sm">
                    Other Files
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {otherFiles.map((file) => (
                      <FileRow
                        key={file.path}
                        file={file}
                        isSelected={selectedIds.has(file.path)}
                        onToggle={onToggle}
                      />
                    ))}
                  </div>
                </div>
              )}

              {notebooks.length === 0 &&
                datasets.length === 0 &&
                scripts.length === 0 &&
                otherFiles.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-[#8b949e]">
                    No files available.
                  </p>
                )}
            </div>
          )}

          <div className="px-6 pb-6">
            <h3 className="font-semibold text-gray-800 dark:text-[#f0f6fc] mb-3">
              Submission Comments{" "}
              <span className="text-gray-500 dark:text-[#8b949e] font-normal">
                (Optional)
              </span>
            </h3>
            <p className="text-sm text-gray-600 dark:text-[#8b949e] mb-4">
              Add notes for the instructor for this checkpoint.
            </p>
            <textarea
              className="w-full p-3 border border-gray-300 dark:border-[#30363d] rounded-lg bg-white dark:bg-[#0d1117] dark:text-[#f0f6fc] focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-[#f0883e]"
              rows={3}
              placeholder="Any notes about this submission..."
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              disabled={!canInteract}
            />
          </div>

          <div className="px-6 py-4 bg-gray-50 dark:bg-[#0d1117] rounded-b-xl flex items-center space-x-3 border-t dark:border-[#30363d]">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              className="dark:border-[#30363d] dark:text-[#c9d1d9] dark:hover:bg-[#30363d]"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={handleSubmit}
              disabled={!canSubmit}
              title={
                isSelectedOverdue ? "Cannot submit to overdue checkpoint" : ""
              }
              className="dark:bg-[#f0883e] dark:hover:bg-[#e0772d] dark:text-white"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />{" "}
                  Submitting...
                </>
              ) : isSelectedOverdue ? (
                "Overdue - Cannot Submit"
              ) : (
                "Submit Assignment"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
